import { defineStore } from "pinia";
import { computed, reactive, ref, watch } from "vue";
import { getLocale } from "../translations";

export const useFilterStore = defineStore("filters", () => {
  const ORDER_ASC = "asc";
  const ORDER_DESC = "desc";

  const order = ref(ORDER_DESC);
  const searchTerm = ref("");

  const filters = reactive({
    categories: {},
    persons: {},
    locations: {},
    dateFrom: null,
    dateTo: null,
  });

  async function init() {
    await fetchCategories();
  }

  async function fetchCategories() {
    const categories = {};

    const response = await fetch(`/api/klarnachrichten/${getLocale()}/filter-options/categories`);
    const json = await response.json();

    if (json.success) {
      for (const id in json.data) {
        categories[id] = json.data[id];
        categories[id]["active"] = false;
      }
    }

    filters.categories = categories;
  }

  const activeCategoryIds = computed(() => {
    return Object.values(filters.categories)
      .filter((category) => category.active)
      .map((category) => category.id);
  });

  const activePersonIds = computed(() => {
    return Object.values(filters.persons)
      .filter((person) => person.active)
      .map((person) => person.id);
  });

  const activeLocationIds = computed(() => {
    return Object.values(filters.locations)
      .filter((location) => location.active)
      .map((location) => location.id);
  });

  const filterQueryParams = computed(() => {
    const data = {};

    if (activeCategoryIds.value.length > 0) {
      data.categories = activeCategoryIds.value;
    }

    if (activePersonIds.value.length > 0) {
      data.persons = activePersonIds.value;
    }

    if (activeLocationIds.value.length > 0) {
      data.locations = activeLocationIds.value;
    }

    if (filters.dateFrom) {
      data.dateFrom = filters.dateFrom.split("T")[0];
    }

    if (filters.dateTo) {
      data.dateTo = filters.dateTo.split("T")[0];
    }

    if (searchTerm.value !== "") {
      data.searchTerm = searchTerm.value;
    }

    data.order = order.value;

    return data;
  });

  const isAnyFilterActive = computed(() => {
    if (filters.dateFrom !== null || filters.dateTo !== null) {
      return true;
    }

    if (activeCategoryIds.value.length !== 0) {
      return true;
    }

    if (activePersonIds.value.length !== 0) {
      return true;
    }

    if (activeLocationIds.value.length !== 0) {
      return true;
    }

    return false;
  });

  function resetFilters() {
    filters.dateFrom = null;
    filters.dateTo = null;
    filters.persons = {};
    filters.locations = {};

    activeCategoryIds.value.forEach((id) => {
      filters.categories[id].active = false;
    });

    window.klarnachrichten.trackMatomoEvent("reset-filters");
  }

  function toggleOrder() {
    order.value = order.value === ORDER_ASC ? ORDER_DESC : ORDER_ASC;
  }

  function applyCollectionFilters(collectionFilters) {
    if (collectionFilters.dateFrom) {
      filters.dateFrom = collectionFilters.dateFrom;
    }

    if (collectionFilters.dateTo) {
      filters.dateTo = collectionFilters.dateTo;
    }

    if (collectionFilters.categories.length > 0) {
      collectionFilters.categories.forEach((category) => {
        filters.categories[category.id].active = true;
      });
    }

    if (collectionFilters.locations.length > 0) {
      collectionFilters.locations.forEach((location) => {
        filters.locations[location.id] = JSON.parse(JSON.stringify(location));
        filters.locations[location.id].active = true;
      });
    }

    if (collectionFilters.persons.length > 0) {
      collectionFilters.persons.forEach((person) => {
        filters.persons[person.id] = JSON.parse(JSON.stringify(person));
        filters.persons[person.id].active = true;
      });
    }
  }

  return {
    init,
    filters,
    isAnyFilterActive,
    resetFilters,
    filterQueryParams,
    searchTerm,
    order,
    toggleOrder,
    applyCollectionFilters,
  };
});
